import { useEffect, useState } from 'react';
import pages from '../../config/pages.json';
import { NavLink, useParams, useLocation } from 'react-router-dom';
import { isMobile } from '../utils/appFunctions';
import { getCurrentPage } from '../utils/NavFunctions.ts';
import { useResponsesStore } from '../stores/Responses';
import { useAppStore } from '../stores/AppStore'
import MyVan from './MyVan.tsx';
import Image from './Image.tsx';
import ExitButton from './ExitButton';

export default function Menu() {
    const { showDrawer, setShowDrawer } = useAppStore(state => state);
    const filteredPages = pages.filter(page => page.showInSidebar !== false);

    useEffect(() => {
        if (isMobile()) setShowDrawer(false);
    }, []);

    // get all pages.group values and remove duplicates - remove where = undefined
    const groups = [...new Set(filteredPages.map(page => page.group))].filter(group => group);
    const show = isMobile() ? showDrawer : true;

    return ( 
        <div 
            className={`drawer ${show ? 'show' : ''} w-full md:!w-[215px] h-full fixed top-0 left-0 z-50 bg-white shadow-md`}
        >
            <div className="h-full px-5 pt-[15px] bg-white flex flex-col overflow-y-auto">
                <div className="mt-[55px]">
                    <MyVan />
                </div>

                <div className="flex-grow text-dusk text-sm font-medium leading-[21px]">
                    <ul className="my-[35px] border-b border-b-mizzle mx-10 md:mx-0">
                        { groups.map((group, id) => (
                            <MenuItem 
                                key={ id } 
                                group={ group } 
                                setShowDrawer={ setShowDrawer }
                            />
                        )) }
                    </ul>
                </div>

                <div className="md:hidden w-full mx-auto mb-32">
                    <ExitButton />
                </div>

                <div className="fixed bottom-0 mb-[25px] mx-[-20px] w-full px-[15px] z-20">
                    <div className="w-full mx-auto flex items-center">
                        <div className="flex-1 flex justify-center">
                            <Image
                                src="Nohma_logo-57_ivvep0"
                                alt="logo"
                                params={{ width: 140, height: 27 }}
                                className="h-auto object-contain"
                            />
                        </div>
                        <img 
                            src="/build/assets/x.svg" 
                            alt="show drawer" 
                            onClick={() => setShowDrawer(false)}
                            className="md:hidden object-contain w-[60px] h-[60px] cursor-pointer bg-cv rounded-full p-[18px]"
                        />
                    </div>
                </div>


            </div>

            <MobileGradient />
        </div>
    );
}

const MenuItem = ({ group, setShowDrawer }) => {
    const { pageId } = useParams();
    const { responses } = useResponsesStore(state => state);
    const [isGroupCompleted, setIsGroupCompleted] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const completed = groupIsCompleted(group);
        setIsGroupCompleted(completed);
    }, [group, location]);

    const hasResponses = (questions) => {
        if (!questions) return false;

        const hasValidResponse = questions.some(question => {
            const response = responses.find(response => response.id === question);
    
            if (response && response.value) return true; 
    
            return false;
        });

        return hasValidResponse;
    };

    // return true if all predecessors are completed
    const arePredecessorsCompleted = (group) => {
        // check if any in this group are completed
        const thisGroupPages = pages.filter(p => p.group === group);

        // check if the previous group is completed - this should be enough
        const firstPageOfGroup = thisGroupPages[0];
        const pageBefore = pages.find(p => p.order === firstPageOfGroup.order - 1);

        if (!pageBefore) return true; // no page before

        return groupIsCompleted(pageBefore.group);
    }

    const groupIsCompleted = (group) => {
        // combine values array for all pages
        const expectedResponses = pages.reduce((acc, page) => {
            if (page.group !== group || !page.responses) return acc;

            if (page.dependencies) {
                // filter out any pages where dependencies are not met
                const dependenciesMet = page.dependencies.every(dep => {
                    const response = responses.find(r => r.id === dep.question);

                    // return if response is in dependency values array
                    return response && dep.values.includes(response.value);
                });

                if (!dependenciesMet) return acc;
            }

            return [...acc, ...page.responses];
        }, []);

        if (expectedResponses.length === 0) return true; // No responses expected

        return isCompleted(expectedResponses);
    }

    // Function to determine if questions for all provided pages are completed
    const isCompleted = (expectedResponses) => {
        // check if all expectedResponses are in responses
        for (let i = 0; i < expectedResponses.length; i++) {
            if (!responses.find(r => r.id === expectedResponses[i])) return false;
        }

        return true; // All checks passed
    };

    const firstPageOfGroup = (group) => {
        // get the first page of the group as ordered by order
        const firstPage = pages.find(p => p.group === group);
        return firstPage ? firstPage.path : '';
    }

    const handleClick = () => {
        if (isMobile()) setShowDrawer(false);
    }

    const currentPage = getCurrentPage(pageId);

    return (
        <li className="py-5 border-t border-mizzle flex justify-between">
            { arePredecessorsCompleted(group) ? (
                <NavLink 
                    to={ firstPageOfGroup(group) }
                    onClick={handleClick}
                    className={ currentPage.group === group ? 'text-dusk font-semibold' : 'text-dusk font-normal' }
                >
                    { group }
                </NavLink>
            ) : (
                <div className="text-gray-500 font-normal">
                    { group }   
                </div> 
            )}

            { isGroupCompleted && 
                <img src="/build/assets/tick.svg" />
            }
        </li>
    );
}

const MobileGradient = () => {
    return (
        <div className="fixed md:hidden bottom-0 w-full h-[150px] bg-gradient-to-t from-calm/100 to-calm/0"></div>
    );
}